import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map, Observable } from "rxjs";
import { arrayToILoadableWrapperArray, createDefaultLoadable, ICameraLicenseLookupResponse, IFamilyLicenseUsage, ILicense, ILicenseV2, IUser } from "../../models";
import { mapCameraLicenseLookupResponse, mapFamilyLicenseUsageResponse, mapLicenseV2Response } from "../../models/helpers/api-mappers";
import { environment } from "../../../../environments/environment";
import { ISubscriptionState } from "../../state/license/license.state";

export type HttpListResponseFailure = { status: string, message: string };

export interface IDistributeLicenseRequest {
    organisation_id: string,
    distributor_id: string,
    original_license_id: string,
    name: string,
    max_number_of_units: string,
    notes?: string
}

export interface IAddLicenseRequest {
    organisation_id: string,
    distributor_id?: string,
    name: string,
    max_number_of_units: string,
    license_type: string,
    price_per_unit: number,
    billing_type: string,
    status: string,
    notes?: string
}

@Injectable({
    providedIn: "root"
})
export class LicenseService {

    readonly endpoints = {
        getAllLicenses: `${environment.apiUrl}/licenses`,
        updateLicense: `${environment.apiUrl}/license/update`,
        addLicense: `${environment.apiUrl}/license/add`,
        distributeLicense: `${environment.apiUrl}/license/distribute`,
        revoke: `${environment.apiUrl}/license/revoke`,
        getAllSubscriptions: `${environment.apiUrl}/license/get/all`,
        getV2Licenses: `${environment.apiUrlV2}/licenses`,
        getCameraLicenses: `${environment.apiUrlV2}/licenses/cameras`,
        getFamilyUsage: (family: string) => `${environment.apiUrlV2}/licenses/family/${encodeURIComponent(family)}/usage`
    };

    private http: HttpClient = inject(HttpClient);

    getLicenses(body: any): Observable<HttpListResponseFailure | any> {
        let endpoint = this.endpoints.getAllLicenses;
        return this.http.get<HttpListResponseFailure | any>(endpoint)
    }

    updateLicense(licenseID: string, newLicenseName: string, newLicenseNotes: string): Observable<HttpListResponseFailure | any> {
        let endpoint = this.endpoints.updateLicense;
        let body = {
            'license_id': licenseID,
            'update_map': {
                "name": newLicenseName,
                "notes": newLicenseNotes
            }
        };
        return this.http.post<HttpListResponseFailure | any>(endpoint, body);
    }

    addLicense(body: IAddLicenseRequest): Observable<ILicense> {
        let request: IAddLicenseRequest = {
            ...body,
            price_per_unit: 0.0,
            billing_type: "ON ACTIVE CAM COUNT",
            status: "NEW_REQUEST"
        }
        let endpoint = this.endpoints.addLicense;
        return this.http.post<HttpListResponseFailure | any>(endpoint, request).pipe(
            map(e => e["data"])
        );
    }

    distributeLicense(body: IDistributeLicenseRequest): Observable<HttpListResponseFailure | any> {
        let endpoint = this.endpoints.distributeLicense;
        return this.http.post<HttpListResponseFailure | any>(endpoint, body);
    }

    revokeLicense(license: ILicense): Observable<ILicense[]> {
        return this.http.post<ILicense[]>(this.endpoints.revoke, {
            "license_id": license.id
        })
    }

    getV2Licenses(): Observable<ILicenseV2[]> {
        return this.http.get<any[]>(this.endpoints.getV2Licenses).pipe(
            map(items => items.map(mapLicenseV2Response))
        );
    }

    getCameraLicenses(cameraIds: string[]): Observable<ICameraLicenseLookupResponse> {
        return this.http.post<any>(this.endpoints.getCameraLicenses, { ids: cameraIds }).pipe(
            map(response => mapCameraLicenseLookupResponse(response))
        );
    }

    getFamilyUsage(family: string): Observable<IFamilyLicenseUsage> {
        return this.http.get<any>(this.endpoints.getFamilyUsage(family)).pipe(
            map(mapFamilyLicenseUsageResponse)
        );
    }

    getSubscriptions(user: IUser): Observable<ISubscriptionState> {
        return this.http.post(this.endpoints.getAllSubscriptions, {
            organisation_id: user.organisation_id
        }).pipe(map((e: any) => ({
            ...createDefaultLoadable(),
            customerCanRequest: e["data"]["customer_can_request"],
            customers: e["data"]["customer_orgs"],
            distributedLicenses: arrayToILoadableWrapperArray(e["data"]["distributed_licenses"].map((e: ILicense) => ({...e, is_distributed_license: true}))),
            distributorId: e["data"]["distributor_id"],
            organisationDistributorId: e["data"]["org_distributor_id"],
            licenses: arrayToILoadableWrapperArray(e["data"]["current_licenses"]),
        })));
    }
}
